import { addDoc, collection, Timestamp } from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../../firebase";
import { Layout, styles } from "../../../../layout";
import { useAuth } from "../../../components/authContext";
import StyledButton from "../../components/button";

const StravaEditorPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const accessToken = localStorage.getItem("stravaAccessToken");
    if (!accessToken) {
      setError("No access token found. Please reconnect to Strava.");
      return;
    }

    const fetchActivities = async () => {
      try {
        const res = await fetch(
          "https://www.strava.com/api/v3/athlete/activities?per_page=25",
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        const data = await res.json();
        if (data.errors || data.message === "Authorization Error") {
          setError("Strava token is invalid or expired. Please reconnect.");
          return;
        }
        setActivities(data);
      } catch (err) {
        console.error("Error fetching Strava data:", err);
        setError("Failed to fetch Strava activities.");
      }
    };

    fetchActivities();
  }, []);

  const handleImport = async (activity: any) => {
    if (!user) return;
    try {
      const distanceMiles = activity.distance / 1609.34;
      const timeMinutes = activity.moving_time / 60;
      const pace = timeMinutes / distanceMiles;
      const createdAt = Timestamp.fromDate(new Date(activity.start_date));

      const workoutData = {
        uid: user.uid,
        createdAt,
        exercises: [
          {
            name: activity.name || "Strava Run",
            mode: "run",
            sets: [
              {
                distance: parseFloat(distanceMiles.toFixed(2)),
                time: parseFloat(timeMinutes.toFixed(1)),
                pace: parseFloat(pace.toFixed(2)),
              },
            ],
          },
        ],
      };

      await addDoc(collection(db, `users/${user.uid}/workouts`), workoutData);
      navigate("/tracker?strava=1");
    } catch (err) {
      console.error("Import error:", err);
      alert("Something went wrong importing this run.");
    }
  };

  const handleReconnect = () => {
    const redirectUri = window.location.hostname.includes("localhost")
      ? "http://localhost:3000/strava"
      : "https://jack-pearson.com/strava";

    window.location.href = `https://www.strava.com/oauth/authorize?client_id=166397&response_type=code&redirect_uri=${redirectUri}&approval_prompt=force&scope=activity:read`;
  };

  return (
    <Layout title="Strava Run Import">
      {error && <p style={{ color: "red", marginBottom: "1rem" }}>{error}</p>}

      <p style={styles.paragraph}>
        Select a recent Strava run to log it to your workout history. You can
        reconnect if your data looks outdated.
      </p>

      <div style={{ marginBottom: "1rem" }}>
        <StyledButton onClick={handleReconnect}>
          🔁 Reconnect Strava
        </StyledButton>
      </div>

      {!activities.length && !error && <p>Loading Strava activities...</p>}

      {activities.map((activity: any) => (
        <div
          key={activity.id}
          style={{
            border: "1px solid #ccc",
            padding: "12px",
            borderRadius: "8px",
            marginBottom: "12px",
            backgroundColor: "#fafafa",
          }}
        >
          <strong>{activity.name}</strong>
          <p style={{ margin: "6px 0", fontSize: "15px" }}>
            {new Date(activity.start_date).toLocaleDateString()} ·{" "}
            {(activity.distance / 1609.34).toFixed(2)} mi ·{" "}
            {(activity.moving_time / 60).toFixed(1)} min · {activity.type}
          </p>
          <StyledButton
            onClick={() => handleImport(activity)}
            style={{ fontSize: "14px" }}
          >
            ✅ Import to Tracker
          </StyledButton>
        </div>
      ))}

      <div
        style={{
          marginTop: "2rem",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <StyledButton
          onClick={() => navigate("/tracker")}
          style={{ backgroundColor: "#444" }}
        >
          ← Back to Tracker
        </StyledButton>

        <StyledButton
          onClick={async () => {
            try {
              const accessToken = localStorage.getItem("stravaAccessToken");
              if (accessToken) {
                await fetch("https://www.strava.com/oauth/deauthorize", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ access_token: accessToken }),
                });
                console.log("✅ Strava token revoked");
              }

              localStorage.removeItem("stravaAccessToken");
              localStorage.removeItem("stravaExpiresAt");

              // OPTIONAL: Remove from Firestore if you're storing it there
              // import { deleteDoc, doc } from "firebase/firestore";
              // await deleteDoc(doc(db, "strava_tokens", user.uid));

              alert("Strava disconnected.");
              navigate("/tracker");
            } catch (err) {
              console.error("Error disconnecting Strava:", err);
              alert("Failed to disconnect from Strava.");
            }
          }}
          style={{ backgroundColor: "#888" }}
        >
          ❌ Disconnect Strava
        </StyledButton>
      </div>
    </Layout>
  );
};

export default StravaEditorPage;
